import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import UniversityGraphView from '../../components/UniversityGraphView';
import FacultyGraphView from '../../components/FacultyGraphView';
import { getUniversityGraph, getFacultyGraph } from '../../services/apiService';
import { UniversityGraphResponse, FacultyGraphResponse } from '../../types/api';

type ViewType = 'university' | 'faculty';

const IS_FACULTY_DETAIL_GRAPH_ENABLED =
  process.env.REACT_APP_FACULTY_DETAIL_GRAPH_ENABLED !== 'false';

export default function InsightsNetworkTab() {
  const { t } = useTranslation('network');
  const navigate = useNavigate();

  const [view, setView] = useState<ViewType>('university');
  const [, setSelectedFaculty] = useState<string | null>(null);

  const [universityData, setUniversityData] = useState<UniversityGraphResponse | null>(null);
  const [universityLoading, setUniversityLoading] = useState<boolean>(true);
  const [universityError, setUniversityError] = useState<string | null>(null);

  const [facultyData, setFacultyData] = useState<FacultyGraphResponse | null>(null);
  const [facultyLoading, setFacultyLoading] = useState<boolean>(false);
  const [facultyError, setFacultyError] = useState<string | null>(null);

  useEffect(() => {
    const loadUniversityGraph = async () => {
      setUniversityLoading(true);
      setUniversityError(null);
      try {
        const data = await getUniversityGraph();
        setUniversityData(data);
      } catch (err: any) {
        setUniversityError(err.message || t('error'));
        console.error('Error loading university graph:', err);
      } finally {
        setUniversityLoading(false);
      }
    };
    loadUniversityGraph();
  }, [t]);

  const handleFacultyClick = useCallback(
    async (abbreviation: string) => {
      setSelectedFaculty(abbreviation);
      setFacultyLoading(true);
      setFacultyError(null);
      setView('faculty');
      try {
        const data = await getFacultyGraph(abbreviation);
        setFacultyData(data);
      } catch (err: any) {
        setFacultyError(err.message || t('error'));
        console.error('Error loading faculty graph:', err);
      } finally {
        setFacultyLoading(false);
      }
    },
    [t],
  );

  const handleBack = useCallback(() => {
    setView('university');
    setSelectedFaculty(null);
    setFacultyData(null);
    setFacultyError(null);
  }, []);

  const handlePersonClick = useCallback(
    (personId: number) => {
      navigate(`/person/${personId}`);
    },
    [navigate],
  );

  return (
    <div className="flex flex-col bg-ctu-bg rounded-xl border border-ctu-border overflow-hidden">
      <div className="py-6 px-8 border-b border-ctu-border bg-ctu-bg">
        <h2 className="m-0 text-[1.4rem] font-bold text-ctu-text">{t('pageTitle')}</h2>
        {view === 'university' && (
          <p className="mt-1.5 mb-0 text-[0.9rem] text-ctu-text-secondary">
            {IS_FACULTY_DETAIL_GRAPH_ENABLED
              ? t('university.subtitle')
              : t('university.subtitleNoFacultyDetail')}
          </p>
        )}
      </div>

      <div className="flex flex-col min-h-[600px]">
        {view === 'university' && (
          <>
            {universityLoading && (
              <div className="flex-1 flex items-center justify-center text-ctu-text-secondary py-12">
                {t('loading')}
              </div>
            )}
            {universityError && (
              <div className="flex-1 flex items-center justify-center text-red-600 py-12">
                {universityError}
              </div>
            )}
            {!universityLoading && !universityError && universityData && (
              <UniversityGraphView
                data={universityData}
                onFacultyClick={
                  IS_FACULTY_DETAIL_GRAPH_ENABLED ? handleFacultyClick : undefined
                }
              />
            )}
          </>
        )}

        {view === 'faculty' && (
          <>
            {facultyLoading && (
              <div className="flex-1 flex items-center justify-center text-ctu-text-secondary py-12">
                {t('loading')}
              </div>
            )}
            {facultyError && (
              <div className="flex-1 flex flex-col items-center justify-center gap-3 py-12">
                <span className="text-red-600">{facultyError}</span>
                <Button variant="outline" size="sm" onClick={handleBack}>
                  {t('faculty.back')}
                </Button>
              </div>
            )}
            {!facultyLoading && !facultyError && facultyData && (
              <FacultyGraphView
                data={facultyData}
                onPersonClick={handlePersonClick}
                onBack={handleBack}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
